<template>
  <button
    :type="type"
    :class="buttonClasses"
    :disabled="disabled"
    @click="handleClick"
  >
    <i v-if="iconLeft && !loading" :class="`icon-${iconLeft}`"></i>
    <span v-if="!iconOnly" class="button__text-wrapper">
      <span class="button__text">{{ text }}</span>
      <span class="button__text">{{ text }}</span>
    </span>
    <i v-if="iconRight && !loading" :class="`icon-${iconRight}`"></i>
    <i class="icon-loader-circle button__spinner"></i>
    <span class="button__overlay"></span>
  </button>
</template>

<script>
export default {
  name: 'Button',
  props: {
    text: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'text', 'reset'].includes(value)
    },
    iconLeft: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    customClass: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses() {
      return [
        'button',
        `button--${this.variant}`,
        this.iconOnly ? 'button--icon-only' : '',
        this.loading ? 'is-loading' : '',
        this.customClass
      ].filter(Boolean).join(' ');
    }
  },
  mounted() {
    this.initializeAnimation();
  },
  methods: {
    handleClick(event) {
      if (!this.disabled) {
        this.$emit('click', event);
      }
    },
    initializeAnimation() {
      const button = this.$el;
      const overlay = button.querySelector('.button__overlay');
      
      if (!overlay || button._btnAnimState) return; // Skip if no overlay or already initialized

      button._btnAnimState = { resetTimer: null };

      button.addEventListener('mouseenter', () => this.startAnimation());
      button.addEventListener('focus', () => this.startAnimation());
      button.addEventListener('mouseleave', () => this.stopAnimation());
      button.addEventListener('blur', () => this.stopAnimation());
    },
    startAnimation() {
      const button = this.$el;
      this.clearTimers();
      button.classList.remove('animate-out', 'animate-reset');
      button.classList.add('animate-in');
    },
    stopAnimation() {
      const button = this.$el;
      const overlay = button.querySelector('.button__overlay');
      if (!overlay) return;

      this.clearTimers();
      button.classList.remove('animate-in', 'animate-reset');
      button.classList.add('animate-out');

      const onEnd = (e) => {
        if (e.propertyName !== 'transform') return;
        this.doReset();
      };

      overlay.addEventListener('transitionend', onEnd, { once: true });

      // Fallback in case transitionend never fires
      button._btnAnimState.resetTimer = setTimeout(() => {
        overlay.removeEventListener('transitionend', onEnd);
        this.doReset();
      }, 450);
    },
    doReset() {
      const button = this.$el;
      this.clearTimers();
      button.classList.remove('animate-in', 'animate-out');
      button.classList.add('animate-reset');

      button._btnAnimState.resetTimer = setTimeout(() => {
        button.classList.remove('animate-reset');
        button._btnAnimState.resetTimer = null;
      }, 20);
    },
    clearTimers() {
      const button = this.$el;
      if (button._btnAnimState?.resetTimer) {
        clearTimeout(button._btnAnimState.resetTimer);
        button._btnAnimState.resetTimer = null;
      }
    }
  },
  beforeUnmount() {
    this.clearTimers();
  }
};
</script>
